import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { setErrorMessage, setNotification } from 'common/notification';
import { FLOW_STATUS_PUBLISHED, setVariables } from 'common/constants';
import { SearchDialogBox } from 'components/UI/SearchDialogBox/SearchDialogBox';
import { CircularProgress } from '@mui/material';
import { GET_FLOWS } from 'graphql/queries/Flow';
import {
  ADD_FLOW_TO_COLLECTION,
  ADD_FLOW_TO_CONTACT,
  ADD_FLOW_TO_WA_GROUP,
  ADD_FLOW_TO_WA_GROUP_COLLECTION,
} from 'graphql/mutations/Flow';

interface StartAFlowProps {
  collectionId: string | undefined;
  entityId?: any;
  setShowFlowDialog: any;
  groups?: boolean;
  channel?: string;
}

export const StartAFlow = ({ collectionId, setShowFlowDialog, groups, entityId, channel }: StartAFlowProps) => {
  const { t } = useTranslation();
  const addFlowForCollectionMutation = groups ? ADD_FLOW_TO_WA_GROUP_COLLECTION : ADD_FLOW_TO_COLLECTION;
  const addFlowMutation = groups ? ADD_FLOW_TO_WA_GROUP : ADD_FLOW_TO_CONTACT;

  const { data: flowsData, loading } = useQuery(GET_FLOWS, {
    variables: setVariables({
      status: FLOW_STATUS_PUBLISHED,
      isActive: true,
      isTemplate: false,
      ...(channel && { channel }),
    }),
    fetchPolicy: 'network-only', // set for now, need to check cache issue
  });

  const [addFlow] = useMutation(addFlowMutation);

  const [addFlowToCollection] = useMutation(addFlowForCollectionMutation);

  const handleFlowSubmit = async (flowId: any) => {
    if (!flowId) return;
    const flowVariables: any = {
      flowId,
    };

    setShowFlowDialog(false);

    if (entityId) {
      if (groups) {
        flowVariables.waGroupId = entityId;
      } else {
        flowVariables.contactId = entityId;
        if (channel) {
          flowVariables.channel = channel;
        }
      }

      try {
        await addFlow({ variables: flowVariables });
        setNotification(t('Flow started successfully.'));
      } catch (error: any) {
        setErrorMessage(error);
      }
    }

    if (collectionId) {
      flowVariables.groupId = collectionId;
      try {
        await addFlowToCollection({ variables: flowVariables });
        setNotification(t('Your flow will start in a couple of minutes.'));
      } catch (error: any) {
        setErrorMessage(error);
      }
    }
  };

  const closeFlowDialogBox = () => {
    setShowFlowDialog(false);
  };

  let flowOptions = [];
  if (flowsData) {
    flowOptions = flowsData.flows;
  }
  if (loading) {
    return <CircularProgress data-testid="loading" />;
  }

  return (
    <SearchDialogBox
      title={t('Select flow')}
      handleOk={handleFlowSubmit}
      handleCancel={closeFlowDialogBox}
      options={flowOptions}
      optionLabel="name"
      multiple={false}
      buttonOk="Start"
      textFieldPlaceholder={t('Select flow')}
      description={t('The contact will be responded as per the messages planned in the flow.')}
    />
  );
};

export default StartAFlow;
